/**
 * Keeps scripts and iframes from running until their category is accepted.
 *
 * Manual mode: the site marks tags itself,
 *   <script type="text/plain" data-bc-category="statistics" src="..."></script>
 *   <iframe data-bc-src="https://www.youtube.com/embed/..." data-bc-category="marketing"></iframe>
 * (data-cookieconsent="statistics" is accepted too, for sites moving from Cookiebot).
 *
 * Auto mode also blocks known trackers by address, both in the HTML and when added by other
 * scripts. Google tags are not blocked: they follow Google Consent Mode instead.
 */

/** Tracker domains (and their subdomains) with the category they need. */
export const KNOWN_TRACKERS: ReadonlyArray<readonly [string, string]> = [
  ["connect.facebook.net", "marketing"],
  ["snap.licdn.com", "marketing"],
  ["static.ads-twitter.com", "marketing"],
  ["analytics.tiktok.com", "marketing"],
  ["s.pinimg.com", "marketing"],
  ["sc-static.net", "marketing"],
  ["bat.bing.com", "marketing"],
  ["cdn.taboola.com", "marketing"],
  ["widgets.outbrain.com", "marketing"],
  ["hotjar.com", "statistics"],
  ["clarity.ms", "statistics"],
  ["mc.yandex.ru", "statistics"],
  ["cdn.mxpnl.com", "statistics"],
  ["cdn.segment.com", "statistics"],
  ["cdn.amplitude.com", "statistics"],
  ["js.hs-analytics.net", "statistics"],
  ["edge.fullstory.com", "statistics"],
];

const BLOCKED_TYPE = "text/plain";

export function categoryForUrl(url: string, base?: string): string | null {
  let host: string;
  try {
    host = new URL(url, base).hostname.toLowerCase();
  } catch {
    return null;
  }
  for (const [domain, category] of KNOWN_TRACKERS) {
    if (host === domain || host.endsWith(`.${domain}`)) {
      return category;
    }
  }
  return null;
}

/** Categories named on a marked tag; "statistics, marketing" needs both. */
export function markedCategories(element: Element): string[] {
  const value =
    element.getAttribute("data-bc-category") ??
    element.getAttribute("data-cookieconsent") ??
    "";
  return value
    .split(",")
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);
}

export class Blocker {
  private observer: MutationObserver | null = null;

  constructor(
    private readonly doc: Document,
    private readonly auto: boolean,
    private readonly allowed: (category: string) => boolean,
  ) {}

  start(): void {
    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => this.inspect(node));
      }
    });
    this.observer.observe(this.doc.documentElement, {
      childList: true,
      subtree: true,
    });

    if (this.auto) {
      this.guardCreatedScripts();
    }
  }

  /** Runs every held tag whose categories are now accepted. */
  release(): void {
    this.doc
      .querySelectorAll(
        "script[data-bc-category], script[data-cookieconsent], iframe[data-bc-src]",
      )
      .forEach((element) => this.activateIfAllowed(element));
  }

  private inspect(node: Node): void {
    if (node instanceof HTMLScriptElement) {
      this.onScript(node);
    } else if (node instanceof HTMLIFrameElement) {
      this.activateIfAllowed(node);
    } else if (node instanceof Element && node.children.length) {
      node
        .querySelectorAll("script, iframe[data-bc-src]")
        .forEach((child) => this.inspect(child));
    }
  }

  private onScript(script: HTMLScriptElement): void {
    if (
      script.hasAttribute("data-bc-category") ||
      script.hasAttribute("data-cookieconsent")
    ) {
      if (script.type === BLOCKED_TYPE) {
        this.activateIfAllowed(script);
      }
      return;
    }
    if (this.auto && script.src) {
      this.blockIfTracker(script);
    }
  }

  private blockIfTracker(script: HTMLScriptElement): void {
    const category = categoryForUrl(script.src, this.doc.baseURI);
    if (!category || this.allowed(category)) {
      return;
    }
    const originalType = script.getAttribute("type");
    if (originalType) {
      script.setAttribute("data-bc-type", originalType);
    }
    script.setAttribute("data-bc-category", category);
    script.setAttribute("type", BLOCKED_TYPE);
    // Firefox runs parser-inserted scripts even after the type change; this event stops it there.
    script.addEventListener("beforescriptexecute", function stop(event) {
      if (script.getAttribute("type") === BLOCKED_TYPE) {
        event.preventDefault();
      }
      script.removeEventListener("beforescriptexecute", stop);
    });
  }

  private activateIfAllowed(element: Element): void {
    const categories = markedCategories(element);
    if (categories.length === 0 || !categories.every(this.allowed)) {
      return;
    }

    if (element instanceof HTMLIFrameElement) {
      const src = element.getAttribute("data-bc-src");
      if (src) {
        element.removeAttribute("data-bc-src");
        element.src = src;
      }
      return;
    }

    if (
      !(element instanceof HTMLScriptElement) ||
      element.type !== BLOCKED_TYPE ||
      !element.parentNode
    ) {
      return;
    }

    // A script only runs when inserted, so replace the held tag with a live copy.
    const live = this.doc.createElement("script");
    for (const attribute of Array.from(element.attributes)) {
      if (attribute.name !== "type" && attribute.name !== "data-bc-type") {
        live.setAttribute(attribute.name, attribute.value);
      }
    }
    const originalType = element.getAttribute("data-bc-type");
    if (originalType) {
      live.setAttribute("type", originalType);
    }
    live.setAttribute("data-bc-released", "");
    live.text = element.text;
    element.parentNode.replaceChild(live, element);
  }

  /** Scripts created by other scripts get their src checked before they are inserted. */
  private guardCreatedScripts(): void {
    const doc = this.doc;
    const originalCreate = doc.createElement;
    const srcProperty = Object.getOwnPropertyDescriptor(
      HTMLScriptElement.prototype,
      "src",
    );
    const setAttribute = Element.prototype.setAttribute;
    if (!srcProperty?.set || !srcProperty.get) {
      return;
    }
    const check = (script: HTMLScriptElement) => {
      if (
        !script.hasAttribute("data-bc-released") &&
        !script.hasAttribute("data-bc-category")
      ) {
        this.blockIfTracker(script);
      }
    };

    doc.createElement = function (
      this: Document,
      tagName: string,
      options?: ElementCreationOptions,
    ) {
      const element = originalCreate.call(this, tagName, options);
      if (element instanceof HTMLScriptElement) {
        Object.defineProperty(element, "src", {
          configurable: true,
          get() {
            return srcProperty.get!.call(this);
          },
          set(value: string) {
            srcProperty.set!.call(this, value);
            check(this as HTMLScriptElement);
          },
        });
        // Code that sets type after src must not un-block a held script: keep the value for later.
        Object.defineProperty(element, "type", {
          configurable: true,
          get() {
            return (this as HTMLScriptElement).getAttribute("type") ?? "";
          },
          set(value: string) {
            const held =
              (this as HTMLScriptElement).getAttribute("type") ===
                BLOCKED_TYPE &&
              !(this as HTMLScriptElement).hasAttribute("data-bc-released");
            setAttribute.call(this, held ? "data-bc-type" : "type", value);
          },
        });
        element.setAttribute = function (name: string, value: string) {
          setAttribute.call(this, name, value);
          if (name.toLowerCase() === "src") {
            check(this as HTMLScriptElement);
          }
        };
      }
      return element;
    } as typeof doc.createElement;
  }
}
